import React, { useState, useEffect } from "react";
import { authApi } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { UsersThree, CheckCircle, XCircle } from "@phosphor-icons/react";

export const AdminPanel = () => {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, u] = await Promise.all([authApi.pending(), authApi.users()]);
      setPending(p); setAll(u);
    } catch { toast.error("Could not load users."); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const act = async (id, kind) => {
    setBusy(true);
    try {
      if (kind === "approve") { await authApi.approve(id); toast.success("User approved — welcome email sent."); }
      else { await authApi.reject(id); toast.success("User rejected."); }
      await load();
    } catch { toast.error("Action failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="admin-panel-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--primary)] hover:bg-[var(--primary)]/10 relative">
          <UsersThree size={14} weight="bold" /> Admin
          {pending.length > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-[var(--alert)] text-white text-[9px] flex items-center justify-center">{pending.length}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-2xl rounded-[4px] max-h-[88vh] overflow-y-auto" data-testid="admin-panel-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><UsersThree size={18} weight="bold" className="text-[var(--primary)]" />User Management</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Approve or reject users who requested access. Approved users can sign in with an email code.</DialogDescription>
        </DialogHeader>

        <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)]">Pending approval ({pending.length})</div>
        <div className="flex flex-col gap-2" data-testid="admin-pending-list">
          {pending.length === 0 && <div className="text-[12px] text-[var(--text-dim)] py-2">No users awaiting approval.</div>}
          {pending.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-3 border border-[var(--border)] rounded-[3px] px-3 py-2" data-testid={`admin-pending-${u.id}`}>
              <div className="min-w-0">
                <div className="text-[13px] font-mono truncate">{u.email}</div>
                <div className="text-[11px] text-[var(--text-dim)]">{u.name || "—"}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button disabled={busy} onClick={() => act(u.id, "approve")} data-testid={`admin-approve-${u.id}`}
                  className="flex items-center gap-1 text-[11px] font-mono text-[var(--success)] hover:underline disabled:opacity-40"><CheckCircle size={14} weight="bold" /> Approve</button>
                <button disabled={busy} onClick={() => act(u.id, "reject")} data-testid={`admin-reject-${u.id}`}
                  className="flex items-center gap-1 text-[11px] font-mono text-[var(--alert)] hover:underline disabled:opacity-40"><XCircle size={14} weight="bold" /> Reject</button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-dim)] mt-3 border-t border-[var(--border)] pt-3">All users ({all.length})</div>
        <div className="flex flex-col gap-1" data-testid="admin-users-list">
          {all.map((u) => (
            <div key={u.id} className="flex items-center justify-between text-[11px] font-mono px-2 py-1">
              <span className="truncate">{u.email}</span>
              <span className={`shrink-0 ${u.status === "approved" ? "text-[var(--success)]" : u.status === "rejected" ? "text-[var(--alert)]" : "text-amber-500"}`}>
                {u.role === "admin" ? "admin" : u.status}
              </span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
